import { useEffect, useMemo, useState } from 'react';

import { adminSession } from '@/lib/api';
import { activeConfig } from '@/lib/environments';

/**
 * /dashboard/api-docs — the /admin/v1 reference.
 *
 * Renders the LIVE OpenAPI document fetched from the backend, not a
 * hand-maintained copy. The spec is the same object GET
 * /admin/v1/openapi.json serves to agents, so this page cannot drift from
 * what a client actually reads — which is the entire reason the spec is
 * hand-written and lives beside its route table.
 *
 * Rendered with a small bespoke walker rather than a Swagger/Redoc
 * dependency: the surface is a dozen endpoints, the document uses a
 * narrow slice of OpenAPI, and pulling in a renderer for this would cost
 * more bundle than the console spends on any actual feature.
 */

type Spec = {
  info?: { title?: string; version?: string; description?: string };
  servers?: Array<{ url: string; description?: string }>;
  paths?: Record<string, Record<string, Operation>>;
};

type Operation = {
  summary?: string;
  description?: string;
  parameters?: Array<{
    name: string;
    in: string;
    required?: boolean;
    description?: string;
    schema?: SchemaNode;
  }>;
  requestBody?: { content?: Record<string, { schema?: SchemaNode }> };
  responses?: Record<string, { description?: string }>;
};

type SchemaNode = {
  type?: string;
  enum?: string[];
  items?: SchemaNode;
  properties?: Record<string, SchemaNode>;
  required?: string[];
  description?: string;
  maxLength?: number;
  maxItems?: number;
  default?: unknown;
  $ref?: string;
  allOf?: SchemaNode[];
};

const METHOD_COLORS: Record<string, string> = {
  get: '#2d7a4f',
  post: '#1f6feb',
  patch: '#9a6700',
  delete: '#a8331f',
};

export function ApiDocsPage() {
  const [spec, setSpec] = useState<Spec | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = adminSession.getToken();
    void (async () => {
      try {
        const res = await fetch(activeConfig().apiBase + '/admin/openapi', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const text = await res.text();
        const data = text ? JSON.parse(text) : null;
        if (!res.ok) {
          setError(data?.error?.message ?? 'Could not load the API reference.');
          return;
        }
        setSpec(data as Spec);
      } catch {
        setError('Could not load the API reference.');
      }
    })();
  }, []);

  const entries = useMemo(() => {
    if (!spec?.paths) return [];
    return Object.entries(spec.paths).flatMap(([path, ops]) =>
      Object.entries(ops).map(([method, op]) => ({ path, method, op })),
    );
  }, [spec]);

  if (error) return <div style={{ color: '#a8331f' }}>{error}</div>;
  if (!spec) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 className="serif" style={{ fontSize: 28, marginBottom: 4 }}>
        {spec.info?.title ?? 'API'}{' '}
        <span style={{ fontSize: 14, color: 'var(--muted)' }}>v{spec.info?.version}</span>
      </h1>
      {spec.info?.description && (
        <p style={{ color: 'var(--muted)', marginTop: 0, lineHeight: 1.6 }}>
          {spec.info.description}
        </p>
      )}

      <SkillDownload />

      {spec.servers && (
        <div style={card}>
          <h2 style={h2}>Base URLs</h2>
          {spec.servers.map((s) => (
            <div key={s.url} style={{ marginBottom: 6 }}>
              <code style={inlineCode}>{s.url}</code>{' '}
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>{s.description}</span>
            </div>
          ))}
          <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 0 }}>
            Authenticate with <code style={inlineCode}>Authorization: Bearer &lt;key&gt;</code>.
            Mint one under Developer.
          </p>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        {entries.map(({ path, method, op }) => (
          <Endpoint key={`${method} ${path}`} path={path} method={method} op={op} />
        ))}
      </div>
    </div>
  );
}

/**
 * Downloads the agent skill — the operating manual for this API, written
 * for an agent rather than a person.
 *
 * Fetched rather than linked because the endpoint is Bearer-authed: a bare
 * <a href> sends no Authorization header and would 401. So we fetch with
 * the session token, then hand the blob to a synthetic anchor.
 *
 * The file the backend serves is compiled from the repo by
 * infra/scripts/sync-agent-skill.mjs and CI fails on drift, so what lands
 * in the download is always what is in the repo — there is no upload step
 * anyone can forget.
 */
const SKILL_FILENAME = 'wydm-admin-skill.md';

function SkillDownload() {
  const [state, setState] = useState<'idle' | 'working' | 'error'>('idle');

  const download = async () => {
    setState('working');
    try {
      const token = adminSession.getToken();
      const res = await fetch(activeConfig().apiBase + '/admin/skill', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setState('error');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // The server names the file (Content-Disposition, exposed via CORS).
      const named = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '');
      a.download = named?.[1] ?? SKILL_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState('idle');
    } catch {
      setState('error');
    }
  };

  return (
    <div style={card}>
      <h2 style={h2}>Agent skill</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
        A markdown guide written for an agent driving this API — auth, the
        environment-stamped keys, the composite vs granular flows, both media
        paths, and the mistakes that cost time. Drop it into your agent's
        skills directory.
      </p>
      <button onClick={() => void download()} disabled={state === 'working'} style={downloadButton}>
        {state === 'working' ? 'Preparing…' : `Download ${SKILL_FILENAME}`}
      </button>
      {state === 'error' && (
        <span style={{ color: '#a8331f', fontSize: 13, marginLeft: 12 }}>
          Could not download — check you hold the docs:read permission.
        </span>
      )}
      <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 0, marginTop: 12 }}>
        Always current: it is compiled from the repo on every deploy, and CI
        fails if the two disagree.
      </p>
    </div>
  );
}

function Endpoint({ path, method, op }: { path: string; method: string; op: Operation }) {
  const [open, setOpen] = useState(false);
  const body = op.requestBody?.content?.['application/json']?.schema;
  return (
    <div style={endpointCard}>
      <button onClick={() => setOpen((v) => !v)} style={endpointHeader}>
        <span style={{ ...methodPill, background: METHOD_COLORS[method] ?? '#555' }}>
          {method.toUpperCase()}
        </span>
        <code style={{ fontSize: 14, minWidth: 0, overflowWrap: 'anywhere' }}>{path}</code>
        <span style={{ color: 'var(--muted)', fontSize: 13, flex: '1 1 160px', textAlign: 'left' }}>
          {op.summary}
        </span>
        <span style={{ color: 'var(--muted)' }}>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {op.description && (
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)' }}>
              {op.description}
            </p>
          )}

          {op.parameters && op.parameters.length > 0 && (
            <>
              <h3 style={h3}>Parameters</h3>
              {op.parameters.map((p) => (
                <div key={p.name} style={paramRow}>
                  <code style={inlineCode}>{p.name}</code>
                  <span style={dim}>
                    {p.in}
                    {p.required ? ' · required' : ''}
                  </span>
                  <SchemaSummary node={p.schema} />
                  {p.description && <div style={dim}>{p.description}</div>}
                </div>
              ))}
            </>
          )}

          {body?.properties && (
            <>
              <h3 style={h3}>Request body</h3>
              <FieldList schema={body} />
            </>
          )}

          {op.responses && (
            <>
              <h3 style={h3}>Responses</h3>
              {Object.entries(op.responses).map(([code, r]) => (
                <div key={code} style={paramRow}>
                  <code style={{ ...inlineCode, color: code.startsWith('2') ? '#2d7a4f' : '#a8331f' }}>
                    {code}
                  </code>
                  <span style={dim}>{r.description}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FieldList({ schema }: { schema: SchemaNode }) {
  const required = new Set(schema.required ?? []);
  return (
    <div>
      {Object.entries(schema.properties ?? {}).map(([name, node]) => (
        <div key={name} style={paramRow}>
          <code style={inlineCode}>{name}</code>
          {required.has(name) && <span style={{ ...dim, color: '#a8331f' }}>required</span>}
          <SchemaSummary node={node} />
          {node.description && <div style={dim}>{node.description}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * One line describing a schema node. Enums are spelled out in full rather
 * than summarised — knowing a field is "a string" is useless; knowing it
 * is one of six named values is the whole reason the enum is in the spec.
 */
function SchemaSummary({ node }: { node?: SchemaNode }) {
  if (!node) return null;
  const bits: string[] = [];
  const enumValues = node.enum ?? node.items?.enum;
  if (node.type === 'array') {
    bits.push(`array of ${node.items?.type ?? 'object'}`);
    if (node.maxItems) bits.push(`max ${node.maxItems}`);
  } else if (node.type) {
    bits.push(node.type);
  } else if (node.$ref) {
    bits.push(node.$ref.split('/').pop() ?? 'object');
  } else if (node.allOf) {
    bits.push(node.allOf.map((a) => a.$ref?.split('/').pop() ?? 'object').join(' + '));
  }
  if (node.maxLength) bits.push(`max ${node.maxLength} chars`);
  if (node.default !== undefined) bits.push(`default ${String(node.default)}`);
  return (
    <span style={dim}>
      {bits.join(' · ')}
      {enumValues && (
        <>
          {bits.length > 0 ? ' · ' : ''}
          {enumValues.map((v) => (
            <code key={v} style={{ ...inlineCode, marginRight: 4 }}>
              {v}
            </code>
          ))}
        </>
      )}
    </span>
  );
}

const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: 20,
  marginTop: 20,
};
const endpointCard: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  marginBottom: 8,
  overflow: 'hidden',
};
const endpointHeader: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: 16,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  textAlign: 'left',
};
const methodPill: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#fff',
  padding: '3px 8px',
  borderRadius: 6,
  minWidth: 58,
  textAlign: 'center',
};
const h2: React.CSSProperties = { fontSize: 15, margin: '0 0 12px' };
const h3: React.CSSProperties = {
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--muted)',
  margin: '18px 0 8px',
};
const paramRow: React.CSSProperties = {
  padding: '8px 0',
  borderTop: '1px solid var(--line)',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: 8,
  fontSize: 13,
};
const inlineCode: React.CSSProperties = {
  background: 'var(--bg)',
  padding: '1px 5px',
  borderRadius: 4,
  fontSize: 12,
  overflowWrap: 'anywhere',
};
const dim: React.CSSProperties = { color: 'var(--muted)', fontSize: 12 };

const downloadButton: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--coral, #e26d5c)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};
